import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { OutputFormulaValue } from 'src/app/models/output-formulas/output-formula-value';
import { evConventionCoverage } from './ev-convention-coverage';
import { filterEvConventions } from './filename-convention-filters';
import { filterStillsSpecs, filterVideosSpecs } from './file-specification-filters';

export interface OutputFormulaEvCreateDialogData {
  organizationId: string;
  fileSpecifications: FileSpecification[];
  filenameConventions: FilenameConvention[];
}

@Component({
  selector: 'app-output-formula-ev-create-dialog',
  templateUrl: './output-formula-ev-create-dialog.component.html',
  styleUrls: ['./output-formula-ev-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputFormulaEvCreateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  readonly evConventionIdsControl = new FormControl<string[]>([], { nonNullable: true });

  readonly stillsSpecIdControl = new FormControl<string | null>(null);
  readonly videosSpecIdControl = new FormControl<string | null>(null);

  stillsSectionOpen = false;
  videosSectionOpen = false;
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputFormulaEvCreateDialogData,
    private readonly dialogRef: MatDialogRef<
      OutputFormulaEvCreateDialogComponent,
      OutputFormula | undefined
    >,
    private readonly service: OutputFormulaService,
  ) {}

  get evConventions(): FilenameConvention[] {
    return filterEvConventions(this.data.filenameConventions);
  }

  get stillsFileSpecifications(): FileSpecification[] {
    return filterStillsSpecs(this.data.fileSpecifications);
  }

  get videosFileSpecifications(): FileSpecification[] {
    return filterVideosSpecs(this.data.fileSpecifications);
  }

  get selectedConventions(): FilenameConvention[] {
    const ids = this.evConventionIdsControl.value;
    return ids
      .map((id) => this.evConventions.find((c) => c.id === id))
      .filter((c): c is FilenameConvention => c !== undefined);
  }

  get stillsAvailable(): boolean {
    return evConventionCoverage(this.selectedConventions).stillsAvailable;
  }

  get videosAvailable(): boolean {
    return evConventionCoverage(this.selectedConventions).videosAvailable;
  }

  get hasAnySectionOpen(): boolean {
    return this.stillsSectionOpen || this.videosSectionOpen;
  }

  get stillsSectionFilled(): boolean {
    return this.stillsSpecIdControl.value !== null;
  }

  get videosSectionFilled(): boolean {
    return this.videosSpecIdControl.value !== null;
  }

  get canCreate(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (this.evConventionIdsControl.value.length === 0) return false;
    if (!this.hasAnySectionOpen) return false;
    if (this.stillsSectionOpen && !this.stillsSectionFilled) return false;
    if (this.videosSectionOpen && !this.videosSectionFilled) return false;
    return true;
  }

  addStillsSection(): void {
    if (this.stillsSectionOpen) return;
    if (!this.stillsAvailable) return;
    this.stillsSectionOpen = true;
  }

  addVideosSection(): void {
    if (this.videosSectionOpen) return;
    if (!this.videosAvailable) return;
    this.videosSectionOpen = true;
  }

  removeStillsSection(): void {
    this.stillsSectionOpen = false;
    this.stillsSpecIdControl.setValue(null);
  }

  removeVideosSection(): void {
    this.videosSectionOpen = false;
    this.videosSpecIdControl.setValue(null);
  }

  create(): void {
    if (!this.canCreate) return;
    const value = this.buildValue();
    if (value === null) return;
    this.submitting = true;
    this.service
      .create(this.data.organizationId, {
        name: this.nameControl.value.trim(),
        value,
        writeReason: { kind: 'initialCreate' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OutputFormula) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildValue(): OutputFormulaValue | null {
    const evConventionIds = this.evConventionIdsControl.value;
    if (evConventionIds.length === 0) return null;
    if (this.stillsSectionOpen && this.videosSectionOpen) {
      const stillsSpecId = this.stillsSpecIdControl.value;
      const videosSpecId = this.videosSpecIdControl.value;
      if (stillsSpecId === null || videosSpecId === null) return null;
      return {
        kind: 'evBoth',
        stillsSpecId,
        videosSpecId,
        evConventionIds,
      };
    }
    if (this.stillsSectionOpen) {
      const stillsSpecId = this.stillsSpecIdControl.value;
      if (stillsSpecId === null) return null;
      return { kind: 'evStills', stillsSpecId, evConventionIds };
    }
    if (this.videosSectionOpen) {
      const videosSpecId = this.videosSpecIdControl.value;
      if (videosSpecId === null) return null;
      return { kind: 'evVideos', videosSpecId, evConventionIds };
    }
    return null;
  }
}
